/**
 * src/integrations/api/client.ts
 *
 * Replaces the Supabase JS client entirely.
 * - JWT tokens stored in localStorage (access + refresh)
 * - Automatic 401 → token refresh → retry
 * - Matches the response shapes the frontend already expects
 */

export const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

// ─── Token storage ────────────────────────────────────────────────────────────
const ACCESS_KEY  = "hams_access_token";
const REFRESH_KEY = "hams_refresh_token";

export const tokenStore = {
  getAccess:  ()      => localStorage.getItem(ACCESS_KEY),
  getRefresh: ()      => localStorage.getItem(REFRESH_KEY),
  set: (access: string, refresh: string) => {
    localStorage.setItem(ACCESS_KEY,  access);
    localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear: () => {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

// ─── Core fetch wrapper ───────────────────────────────────────────────────────
let isRefreshing = false;
let refreshQueue: Array<(token: string) => void> = [];

async function refreshAccessToken(): Promise<string | null> {
  const refresh = tokenStore.getRefresh();
  if (!refresh) return null;

  const res = await fetch(`${API_BASE}/api/auth/token/refresh/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh }),
  });
  if (!res.ok) {
    tokenStore.clear();
    return null;
  }
  const data = await res.json();
  tokenStore.set(data.access, data.refresh ?? refresh);
  return data.access;
}

export async function apiFetch(
  path: string,
  init: RequestInit = {},
  retry = true
): Promise<Response> {
  const token = tokenStore.getAccess();
  const headers = new Headers(init.headers);
  if (!(init.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });

  const isAuthEndpoint = path.includes("/api/auth/login") || path.includes("/api/auth/token/refresh");
  if (res.status === 401 && retry && !isAuthEndpoint) {
    // Queue concurrent requests during refresh
    if (isRefreshing) {
      return new Promise((resolve) => {
        refreshQueue.push((newToken) => {
          headers.set("Authorization", `Bearer ${newToken}`);
          resolve(fetch(`${API_BASE}${path}`, { ...init, headers }));
        });
      });
    }
    isRefreshing = true;
    const newToken = await refreshAccessToken();
    isRefreshing = false;

    if (newToken) {
      refreshQueue.forEach((cb) => cb(newToken));
      refreshQueue = [];
      headers.set("Authorization", `Bearer ${newToken}`);
      return fetch(`${API_BASE}${path}`, { ...init, headers });
    } else {
      // Refresh failed → redirect to login and clear store
      tokenStore.clear();
      import("@/store/useAuthStore").then((mod) => {
        mod.useAuthStore.getState().clearAuth();
      });
      window.location.assign("/auth");
      return res;
    }
  }

  return res;
}

// ─── Typed helpers ────────────────────────────────────────────────────────────
export async function apiGet<T>(path: string, params?: Record<string, string | number | boolean | null | undefined>): Promise<T> {
  let fullPath = path;
  if (params) {
    const searchParams = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== null && v !== undefined && v !== "") {
        searchParams.set(k, String(v));
      }
    }
    const qs = searchParams.toString();
    if (qs) {
      fullPath = `${path}${path.includes('?') ? '&' : '?'}${qs}`;
    }
  }
  const res = await apiFetch(fullPath, { method: "GET" });
  if (!res.ok) throw new Error(await extractErrorMessage(res));
  return res.json();
}

export async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const isFd = body instanceof FormData;
  const res = await apiFetch(path, { method: "POST", body: isFd ? body : JSON.stringify(body) });
  if (!res.ok) throw new Error(await extractErrorMessage(res));
  return res.json();
}

export async function apiPatch<T>(path: string, body: unknown): Promise<T> {
  const isFd = body instanceof FormData;
  const res = await apiFetch(path, { method: "PATCH", body: isFd ? body : JSON.stringify(body) });
  if (!res.ok) throw new Error(await extractErrorMessage(res));
  return res.json();
}

export async function apiPut<T>(path: string, body: unknown): Promise<T> {
  const isFd = body instanceof FormData;
  const res = await apiFetch(path, { method: "PUT", body: isFd ? body : JSON.stringify(body) });
  if (!res.ok) throw new Error(await extractErrorMessage(res));
  return res.json();
}

export async function apiDelete(path: string): Promise<void> {
  const res = await apiFetch(path, { method: "DELETE" });
  if (!res.ok) {
    if (res.status === 404) return; // Already deleted
    throw new Error(await extractErrorMessage(res));
  }
}

function getFirstErrorString(obj: unknown, prefix = ''): string | null {
  if (typeof obj === 'string') return prefix ? `${prefix}: ${obj}` : obj;
  if (Array.isArray(obj)) {
    for (const item of obj) {
      const s = getFirstErrorString(item, prefix);
      if (s) return s;
    }
  } else if (typeof obj === 'object' && obj !== null) {
    for (const [key, val] of Object.entries(obj)) {
      const niceKey = key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');
      const newPrefix = prefix ? `${prefix} > ${niceKey}` : niceKey;
      const s = getFirstErrorString(val, newPrefix);
      if (s) return s;
    }
  }
  return null;
}

async function extractErrorMessage(res: Response): Promise<string> {
  const text = await res.text();
  try {
    const data = JSON.parse(text);
    if (data.detail) return String(data.detail);
    if (data.non_field_errors) return [].concat(data.non_field_errors).join(", ");
    
    const firstStr = getFirstErrorString(data);
    if (firstStr) return firstStr;
    
    return JSON.stringify(data);
  } catch {
    // If it's an HTML response (e.g., generic 500 page or Django debug page), don't show the raw HTML
    if (text.trim().toLowerCase().startsWith("<!doctype html") || text.trim().toLowerCase().startsWith("<html")) {
      return `Server error (${res.status}): An unexpected error occurred on the server. Please check server logs.`;
    }
  }
  return text || `Server error (${res.status})`;
}

// ─── Paginated list helper ─────────────────────────────────────────────────────
export type DRFPage<T> = {
  count:    number;
  next:     string | null;
  previous: string | null;
  results:  T[];
};

export async function apiList<T>(path: string, params?: Record<string, string | number | boolean | null | undefined>): Promise<DRFPage<T>> {
  const searchParams = new URLSearchParams();
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v !== null && v !== undefined && v !== "") {
        searchParams.set(k, String(v));
      }
    }
  }
  const qs = searchParams.toString();
  const fullPath = `${path}${qs ? "?" + qs : ""}`;
  const response = await apiGet<any>(fullPath);

  // If DRF returns a paginated dictionary, it must have .results
  if (response && typeof response === "object" && !Array.isArray(response) && Array.isArray(response.results)) {
    return response as DRFPage<T>;
  }

  // If DRF returns a raw array (pagination_class = None)
  if (Array.isArray(response)) {
    return {
      count: response.length,
      next: null,
      previous: null,
      results: response as T[],
    };
  }

  // If neither, fail loudly
  throw new Error(
    `API Shape Mismatch at ${path}: Expected DRF paginated object { results: [...] } or array, but received: ${JSON.stringify(response).slice(0, 100)}...`
  );
}
